import * as React from "react";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IPasswordRequirement from "../../types/IPasswordRequirement";

import PasswordRequirementStatus from "./PasswordRequirementStatus";

interface IChangePasswordInputProps extends ITranslationProps {
    onSubmit: (password: string, confirmPassword: string) => void;
    onPasswordChanged: (password: string, confirmPassword: string) => void;
    passwordRequirements: IPasswordRequirement[];
    doValidation: boolean;
    autoFocus?: boolean;
}

interface IChangePasswordInputState {
    password: string;
    confirmPassword: string;
}

class ChangePasswordInput extends React.Component<IChangePasswordInputProps, IChangePasswordInputState> {
    private formElement: HTMLFormElement|null;

    constructor(props: IChangePasswordInputProps) {
        super(props);

        this.state = { 
            password: "", 
            confirmPassword: "",
        };

        this.onPasswordChange = this.onPasswordChange.bind(this);
        this.onConfirmPasswordChange = this.onConfirmPasswordChange.bind(this);
        this.inputKeypress = this.inputKeypress.bind(this);
    }

    // -----------------------------------------------------------
    // Render Methods
    // -----------------------------------------------------------
    public render() {
        return (
            <div className="row">
                <div className="col-sm">
                    <form ref={ref => this.formElement = ref} className={this.props.doValidation ? "was-validated" : "needs-validation"} noValidate={true}>
                        <div className="form-group">
                            <label>{this.props.t("ChangePasswordInput.Password")}</label>
                            <input 
                                type="password" 
                                className="form-control" 
                                placeholder={this.props.t("ChangePasswordInput.Password")}
                                value={this.state.password} 
                                onChange={this.onPasswordChange} 
                                onKeyPress={this.inputKeypress}
                                required={true} 
                                autoFocus={this.props.autoFocus} />

                            <div className="invalid-feedback">
                                {this.props.t("ChangePasswordInput.PasswordValidationMessage")}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>{this.props.t("ChangePasswordInput.ConfirmPassword")}</label>
                            <input 
                                type="password" 
                                className="form-control" 
                                placeholder={this.props.t("ChangePasswordInput.ConfirmPassword")}
                                value={this.state.confirmPassword} 
                                onChange={this.onConfirmPasswordChange} 
                                onKeyPress={this.inputKeypress}
                                required={true} />

                            <div className="invalid-feedback">
                                {this.props.t("ChangePasswordInput.PasswordValidationMessage")}
                            </div>
                            {(this.props.doValidation && this.state.password !== this.state.confirmPassword) &&
                                <div className="alert alert-warning">
                                    {this.props.t("ChangePasswordInput.PasswordDoNotMatch")}
                                </div>
                            }
                        </div>
                    </form>
                </div>
                <div className="col-sm">
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title">{this.props.t("ChangePasswordInput.PasswordRequirementsTitle")}</h5>
                            <div className="card-text">
                                {this.renderPasswordRequirements()}
                                {this.renderConfirmPasswordRequirement()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderPasswordRequirements(): JSX.Element[] {
        const details = this.props.passwordRequirements.map((r) => {
            return (
                <PasswordRequirementStatus key={r.requirementType} passwordRequirement={r} />
            );
        });

        return details;
    }

    private renderConfirmPasswordRequirement(): JSX.Element {
        const passwordsMatch: boolean = this.state.password !== "" && this.state.confirmPassword !== "" && this.state.password === this.state.confirmPassword;
        const passwordRequirement: IPasswordRequirement = {
            description: this.props.t("ChangePasswordInput.ConfirmPasswordRequirement"),
            passwordMeetsRequirement: passwordsMatch,
            requirementType: 0,
            minimumCharacters: 0
        };

        return (
            <PasswordRequirementStatus passwordRequirement={passwordRequirement} />
        );
    }

    // -----------------------------------------------------------
    // Events
    // -----------------------------------------------------------
    private onPasswordChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ password: event.target.value }, () => {
            this.alertParentThatPasswordChanged();
        });
    }

    private onConfirmPasswordChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ confirmPassword: event.target.value }, () => {
            this.alertParentThatPasswordChanged();
        });
    }

    private inputKeypress(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();

            // Do form validation to show any error messages for required fields
            if (this.formElement) {
                this.formElement.checkValidity();
            }

            // Let the parent function know the password changed
            this.props.onSubmit(this.state.password, this.state.confirmPassword);
        }
    }

    // -----------------------------------------------------------
    // Helper Methods
    // -----------------------------------------------------------
    private alertParentThatPasswordChanged() {
        this.props.onPasswordChanged(this.state.password, this.state.confirmPassword);
    }
}

export default translateComponent(ChangePasswordInput);



// WEBPACK FOOTER //
// ./src/core/ChangePasswordInput/ChangePasswordInput.tsx